import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "media-thumbnail flex justify-center items-center min-w-[4rem] w-16 h-16"
}
const _hoisted_2 = {
  key: 1,
  class: "w-full flex items-center justify-center h-full gap-3"
}
const _hoisted_3 = { class: "flex justify-centerflex justify-center items-center w-16 h-16 min-w-[4rem]" }
const _hoisted_4 = {
  key: 2,
  class: "flex flex-col justify-between overflow-hidden text-ellipsis select-none w-full selection:bg-transparent"
}
const _hoisted_5 = { class: "text-sm font-medium text-ellipsis text-emphasised-text overflow-hidden whitespace-nowrap select-none" }
const _hoisted_6 = { class: "text-sm text-deEmphasised-text text-ellipsis overflow-hidden whitespace-nowrap select-none" }

import { NodeViewWrapper } from '@tiptap/vue-3';
import {
  InlineMediaType,
  PlaceholderStatus,
} from '@superthread-com/extension-inline-media/src/index';
import type { Node } from '@tiptap/pm/model';
import useTranslate from '@/utilities/composables/useTranslate';
import { computed, watch } from 'vue';
import CircularProgress from '@/components/elements/CircularProgress';
import { useTimeoutFn } from '@vueuse/core';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'GenericMediaPlaceholder',
  props: {
    node: {},
    updateAttributes: {},
    deleteNode: {}
  },
  setup(__props: any) {

/**
 * NOTE: This component relies heavily on watchers since not every information
 * is available before rendering. Information such as wether we own the upload
 * or not are only computed later, so we assume the initial state is incorrect,
 * thus any logic in onMounted is assumed to be unreliable.
 */
const { translate } = useTranslate();

const props = __props;

const store = useStore();

/**
 * Local progress. It only changes values if the current
 * client owns the upload.
 */
const progress = computed(
  () =>
    store.state.editors.uploading.find((u: any) => u.id === props.node.attrs.uploadId)?.progress ??
    0
);

/**
 * Indicates wether we're the ones currently uploading this file.
 */
const owner = computed(
  () => !!store.state.editors.uploading.find((u: any) => u.id === props.node.attrs.uploadId)
);

watch(progress, (newValue) => {
  props.updateAttributes({
    uploadProgress: newValue,
  });
});

/**
 * This timer is immediately started, since we cannot tell
 * from the start wether we own the upload or not - the
 * store which we rely upon updates later.
 */
const { start: startCheck, stop: stopCheck } = useTimeoutFn(() => {
  if (!owner.value) {
    props.updateAttributes({
      status: PlaceholderStatus.CheckActive,
    });
  }
}, 3000);

/**
 * This timer does not start immediately, since this is a function that
 * transtitions the state from CHECK_ACTIVE to INACTIVE.
 * The initial state is ACTIVE.
 */
const { start: startInactivation, stop: stopInactivation } = useTimeoutFn(
  () => {
    props.updateAttributes({
      status: PlaceholderStatus.Inactive,
    });
  },
  3000,
  { immediate: false }
);

/**
 * The collaborative progress. This does not require for
 * the client to be an owner to see the value.
 */
const syncedProgress = computed(() => props.node.attrs.progress);

watch(syncedProgress, () => {
  /**
   * If the progress changes, it is a clear sign of activity.
   * Update the attribute to ACTIVE, if it is in the CHECK_ACTIVE state.
   */
  if (props.node.attrs.status === PlaceholderStatus.CheckActive) {
    props.updateAttributes({
      status: PlaceholderStatus.Active,
    });
  }
});

const syncedStatus = computed(() => props.node.attrs.status);

watch(syncedStatus, (newStatus) => {
  switch (newStatus) {
    /**
     * The new status is ACTIVE
     * Stop the pending inactivation.
     * Reset the check timer.
     */
    case PlaceholderStatus.Active:
      stopInactivation();
      stopCheck();
      startCheck();
      break;
    /**
     * The new status is CHECK_ACTIVE
     */
    case PlaceholderStatus.CheckActive:
      if (owner.value) {
        /**
         * If we own the upload:
         * Reset to ACTIVE.
         */
        props.updateAttributes({
          status: PlaceholderStatus.Active,
        });
      } else {
        /**
         * If we do not own the upload:
         * Stop the pending check.
         * Start the inactivation timer.
         */
        stopCheck();
        startInactivation();
      }
      break;
    /**
     * The new status is INACTIVE
     * Stop all timers.
     * Remove the node.
     */
    case PlaceholderStatus.Inactive:
      stopCheck();
      stopInactivation();
      props.deleteNode();
      if (owner.value) {
        store.dispatch('removeEditorUpload', props.node.attrs);
      }
      break;
  }
});

/**
 * Function used for shortening the media type check, so
 * that the component is more readable.
 */
function mt(mediaType: InlineMediaType) {
  return props.node?.attrs?.mediaType === mediaType;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NodeViewWrapper), {
    class: _normalizeClass(["generic-file-wrapper", {
      '!w-full': mt(_unref(InlineMediaType).Video),
    }])
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["generic-media-file-placeholder gap-2", {
        'h-[24.609rem] w-full max-w-full justify-center !p-0': mt(_unref(InlineMediaType).Video),
        'box-border w-[260px] max-w-[260px] min-h-[80px] gap-2.5': mt(_unref(InlineMediaType).File),
      }])
      }, [
        (mt(_unref(InlineMediaType).File))
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
              _createVNode(_unref(CircularProgress), {
                "transition-duration": 300,
                radius: 16,
                "stroke-width": 4,
                value: props.node.attrs.uploadProgress ?? 0,
                class: "card-attachment-load-spiner"
              }, null, 8, ["value"])
            ]))
          : _createCommentVNode("", true),
        _cache[1] || (_cache[1] = _createTextVNode()),
        (mt(_unref(InlineMediaType).Video))
          ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
              _createElementVNode("span", _hoisted_3, [
                _createVNode(_unref(CircularProgress), {
                  "transition-duration": 300,
                  radius: 16,
                  "stroke-width": 4,
                  value: props.node.attrs.uploadProgress ?? 0,
                  class: "card-attachment-load-spiner"
                }, null, 8, ["value"])
              ]),
              _createTextVNode(" " + _toDisplayString(props.node.attrs.uploadProgress ?? 0) + "% " + _toDisplayString(_unref(translate)('uploaded').toLowerCase()), 1)
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.node.attrs.fileName), 1),
              _cache[0] || (_cache[0] = _createTextVNode()),
              _createElementVNode("span", _hoisted_6, _toDisplayString(props.node.attrs.uploadProgress ?? 0) + "% " + _toDisplayString(_unref(translate)('uploaded').toLowerCase()), 1)
            ]))
      ], 2)
    ]),
    _: 1
  }, 8, ["class"]))
}
}

})